import React from 'react';
import { Cloud, HardDrive, AlertTriangle, ChevronLeft, ChevronRight, SearchX, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Toolbar } from './Toolbar';
import { TableSkeleton } from './TableSkeleton';
import { EmptyState } from './EmptyState';

export interface ColumnDef<T> {
  key: string;
  header: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
  sortable?: boolean;
  render: (item: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  getItemKey: (item: T) => string;
  getSyncState?: (item: T) => 'synced' | 'pending' | 'conflict' | string | undefined;
  renderMobileCard?: (item: T) => React.ReactNode;
  extraToolbar?: React.ReactNode;
  
  // Custom Table Hook State (Optional)
  tableState?: {
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    sortBy: string;
    setSortBy: (s: string) => void;
    sortOrder: 'asc' | 'desc';
    toggleSortOrder: () => void;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    pageSize: string;
    setPageSize: (size: string) => void;
    pageSizeOptions: { value: string; label: string }[];
    paginatedData: T[];
    totalCount: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    sortOptions: { value: string; label: string }[];
  };

  isLoading?: boolean;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: any;
  actionButton?: React.ReactNode;
}

/**
 * Indicador sutil de status de sincronización con Tooltip
 */
export function SyncStateIcon({ state }: { state?: string }) {
  if (!state) return null;

  const configs: Record<string, { icon: any; colorClass: string; tooltip: string }> = {
    synced: {
      icon: Cloud,
      colorClass: 'text-emerald-500 hover:text-emerald-600 bg-emerald-500/10',
      tooltip: 'Guardado en la nube',
    },
    pending: {
      icon: HardDrive,
      colorClass: 'text-amber-500 hover:text-amber-600 bg-amber-500/10',
      tooltip: 'Guardado en este equipo (Pendiente de respaldo)',
    },
    conflict: {
      icon: AlertTriangle,
      colorClass: 'text-error hover:text-red-600 bg-error/10',
      tooltip: 'Conflicto de sincronización',
    },
  };

  const config = configs[state] || configs.synced;
  const Icon = config.icon;

  return (
    <div className="relative group inline-flex items-center">
      <span className={`inline-flex items-center justify-center p-1 rounded-md transition-all cursor-help ${config.colorClass}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      {/* Subtle hover tooltip */}
      <div className="absolute left-1/2 -top-8 -translate-x-1/2 hidden group-hover:flex items-center px-2 py-1 bg-slate-800 text-white text-[10px] font-semibold rounded-md whitespace-nowrap shadow-lg z-50 pointer-events-none animate-fade-in">
        {config.tooltip}
        <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
      </div>
    </div>
  );
}

export function DataTable<T>({
  data,
  columns,
  getItemKey,
  getSyncState,
  renderMobileCard,
  extraToolbar,
  tableState,
  isLoading = false,
  searchPlaceholder = 'Buscar...',
  emptyTitle = 'No hay registros',
  emptyDescription = 'No se encontraron datos disponibles.',
  emptyIcon,
  actionButton,
}: DataTableProps<T>) {
  const displayData = tableState ? tableState.paginatedData : data;
  const totalCount = tableState ? tableState.totalCount : data.length;
  const isSearchActive = Boolean(tableState?.searchQuery && tableState.searchQuery.trim().length > 0);

  const handleHeaderSort = (key: string) => {
    if (!tableState) return;
    if (tableState.sortBy === key) {
      tableState.toggleSortOrder();
    } else {
      tableState.setSortBy(key);
    }
  };

  return (
    <div className="space-y-4">
      {/* ── Single Unified Toolbar Header ── */}
      {tableState && (
        <Toolbar
          searchQuery={tableState.searchQuery}
          onSearchChange={tableState.setSearchQuery}
          searchPlaceholder={searchPlaceholder}
          sortBy={tableState.sortBy}
          onSortByChange={tableState.setSortBy}
          sortOptions={tableState.sortOptions}
          sortOrder={tableState.sortOrder}
          onSortOrderToggle={tableState.toggleSortOrder}
          pageSize={tableState.pageSize}
          onPageSizeChange={tableState.setPageSize}
          pageSizeOptions={tableState.pageSizeOptions}
        >
          {extraToolbar}
        </Toolbar>
      )}

      {/* ── Loading Skeleton ── */}
      {isLoading ? (
        <div className="border border-border bg-surface rounded-2xl p-6 shadow-xs">
          <TableSkeleton rows={6} cols={columns.length} />
        </div>
      ) : isSearchActive && totalCount === 0 ? (
        /* ── Tier 2: Search Query Active with 0 Matches ── */
        <div className="border border-border bg-surface rounded-2xl p-8 shadow-xs text-center">
          <EmptyState
            type="no-search"
            icon={SearchX}
            title="Sin resultados para tu búsqueda"
            description={`No encontramos registros que coincidan con "${tableState?.searchQuery}".`}
            actionButton={
              <button
                type="button"
                onClick={() => tableState?.setSearchQuery('')}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold bg-surface-secondary border border-border text-text-primary rounded-xl hover:bg-surface-tertiary transition-all cursor-pointer shadow-xs"
              >
                <X className="h-3.5 w-3.5 text-text-tertiary" />
                <span>Limpiar búsqueda</span>
              </button>
            }
          />
        </div>
      ) : totalCount === 0 ? (
        /* ── Tier 1: Truly Empty Database (0 Records) ── */
        <div className="border border-border bg-surface rounded-2xl p-8 shadow-xs text-center">
          <EmptyState
            type="empty"
            title={emptyTitle}
            description={emptyDescription}
            icon={emptyIcon}
            actionButton={actionButton}
          />
        </div>
      ) : (
        /* ── Main Data View ── */
        <div className="space-y-4">
          {/* Mobile Card View (<768px) */}
          <div className="block md:hidden space-y-3">
            {displayData.map((item) => {
              const key = getItemKey(item);
              const syncState = getSyncState ? getSyncState(item) : undefined;

              if (renderMobileCard) {
                return (
                  <div key={key} className="relative">
                    {renderMobileCard(item)}
                  </div>
                );
              }

              // Default Mobile Card layout if renderMobileCard is not provided
              return (
                <div key={key} className="p-4 rounded-2xl border border-border bg-surface shadow-xs space-y-3">
                  <div className="flex items-center justify-between border-b border-border/40 pb-2">
                    <div className="font-bold text-sm text-text-primary">
                      {columns[0] ? columns[0].render(item) : key}
                    </div>
                    {syncState && <SyncStateIcon state={syncState} />}
                  </div>
                  <div className="space-y-1.5 text-xs">
                    {columns.slice(1).map((col) => (
                      <div key={col.key} className="flex justify-between items-center py-0.5">
                        <span className="text-text-tertiary font-medium">{col.header}:</span>
                        <span className="text-text-primary font-semibold">{col.render(item)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View (>=768px) */}
          <div className="hidden md:block border border-border bg-surface rounded-2xl shadow-xs">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="text-xs uppercase text-text-tertiary">
                <tr>
                  {getSyncState && (
                    <th className="sticky top-0 z-10 w-10 px-3 py-3.5 font-semibold text-center bg-surface border-b border-border shadow-xs rounded-tl-2xl">
                      Sync
                    </th>
                  )}
                  {columns.map((col, index) => {
                    const isSortable = col.sortable !== false && col.key !== 'acciones';
                    const isSorted = tableState?.sortBy === col.key;
                    const sortOrder = tableState?.sortOrder || 'asc';
                    const isFirstColumn = !getSyncState && index === 0;
                    const isLastColumn = index === columns.length - 1;

                    return (
                      <th
                        key={col.key}
                        onClick={() => isSortable && handleHeaderSort(col.key)}
                        className={`sticky top-0 z-10 px-6 py-3.5 font-semibold bg-surface border-b border-border shadow-xs ${
                          isFirstColumn ? 'rounded-tl-2xl' : ''
                        } ${isLastColumn ? 'rounded-tr-2xl' : ''} ${
                          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                        } ${
                          isSortable ? 'cursor-pointer select-none hover:text-text-primary transition-colors group' : ''
                        } ${col.className || ''}`}
                      >
                          <div className={`inline-flex items-center gap-1.5 ${
                            col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : 'justify-start'
                          }`}>
                            <span>{col.header}</span>
                            {tableState && isSortable && (
                              <span className="shrink-0 text-text-tertiary">
                                {isSorted ? (
                                  sortOrder === 'asc' ? (
                                    <ArrowUp className="h-3.5 w-3.5 text-brand-orange" />
                                  ) : (
                                    <ArrowDown className="h-3.5 w-3.5 text-brand-orange" />
                                  )
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-30 group-hover:opacity-100 transition-opacity" />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayData.map((item) => {
                    const key = getItemKey(item);
                    const syncState = getSyncState ? getSyncState(item) : undefined;

                    return (
                      <tr key={key} className="hover:bg-surface-hover/30 transition-colors">
                        {getSyncState && (
                          <td className="w-10 px-3 py-4 text-center">
                            <SyncStateIcon state={syncState} />
                          </td>
                        )}
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={`px-6 py-4 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${col.className || ''}`}
                          >
                            {col.render(item)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          {/* ── Pagination Controls ── */}
          {tableState && tableState.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <div className="text-xs font-semibold text-text-tertiary">
                Mostrando{' '}
                <span className="font-bold text-text-primary">{tableState.startIndex}</span> a{' '}
                <span className="font-bold text-text-primary">{tableState.endIndex}</span> de{' '}
                <span className="font-bold text-text-primary">{tableState.totalCount}</span> registros
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={tableState.page <= 1}
                  onClick={() => tableState.setPage((p) => Math.max(1, p - 1))}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-border bg-surface text-text-primary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Anterior</span>
                </button>
                <span className="text-xs font-bold text-text-secondary px-2">
                  Página {tableState.page} de {tableState.totalPages}
                </span>
                <button
                  type="button"
                  disabled={tableState.page >= tableState.totalPages}
                  onClick={() => tableState.setPage((p) => Math.min(tableState.totalPages, p + 1))}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-border bg-surface text-text-primary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <span>Siguiente</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
